import React, { useState } from 'react';
import { Button, Alert, Box, Typography, Paper } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { CloudFunctions } from '../FirebaseAuth/firebase';
import { Refresh as RefreshIcon, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';

/**
 * Admin Profile Manager Component
 * Allows kwallAdmins to manage organization profiles in bulk
 */
const AdminProfileManager = ({ currentUser }) => {
  const [isBulkRefreshing, setIsBulkRefreshing] = useState(false);
  const [bulkResult, setBulkResult] = useState('');
  const [bulkError, setBulkError] = useState('');

  // Only show to kwallAdmin users
  if (!currentUser?.role?.includes('kwallAdmin')) {
    return null;
  }

  const handleBulkRefresh = async () => {
    setIsBulkRefreshing(true);
    setBulkError('');
    setBulkResult('');

    try {
      const refreshAllProfiles = httpsCallable(CloudFunctions, 'orgProfileEnhancer-refreshAllOrganizationProfiles');
      const response = await refreshAllProfiles({});
      
      setBulkResult(`Bulk refresh complete! Processed: ${response.data.processed} organizations, Errors: ${response.data.errors}`);
    } catch (error) {
      console.error('Error bulk refreshing organization profiles:', error);
      setBulkError(error.message || 'Failed to bulk refresh organization profiles');
    }

    setIsBulkRefreshing(false);
  };

  return (
    <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.light', border: '1px solid', borderColor: 'info.main' }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'info.dark' }}>
        <AutoAwesomeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Admin: Organization Profile Manager
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Enhanced organization profiles include industry data from Diffbot, structured summaries, 
        and specific recommendations based on actual portfolio data.
      </Typography>
      
      {bulkError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {bulkError}
        </Alert>
      )}
      
      {bulkResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {bulkResult}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="info"
          onClick={handleBulkRefresh}
          disabled={isBulkRefreshing}
          startIcon={<RefreshIcon />}
          size="medium"
        >
          {isBulkRefreshing ? 'Refreshing All Organizations...' : 'Refresh All Organization Profiles'}
        </Button>
      </Box>
      
      <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
        This will refresh industry data, generate structured summaries, and create specific recommendations 
        for up to 10 organizations. Individual organization refresh buttons are available on each organization card.
      </Typography>
    </Paper>
  );
};

export default AdminProfileManager;